// Main game state hook
import { useState, useCallback, useEffect } from 'react';
import { useGameApi } from './useGameApi';
import {
  Story,
  Character,
  Scene,
  Choice,
  CharacterClass
} from '../types';

export type GamePhase = 'new' | 'playing' | 'complete' | 'abandoned';

interface GameState {
  phase: GamePhase;
  sessionId: string | null;
  story: Story | null;
  character: Character | null;
  currentScene: Scene | null;
  choices: Choice[];
  sceneHistory: Scene[];
  waitingForRoll: boolean;
  pendingChoiceIndex: number | null;
  leveledUp: boolean;
  previousLevel: number;
}

const initialState: GameState = {
  phase: 'new',
  sessionId: null,
  story: null,
  character: null,
  currentScene: null,
  choices: [],
  sceneHistory: [],
  waitingForRoll: false,
  pendingChoiceIndex: null,
  leveledUp: false,
  previousLevel: 1,
};

export function useGame() {
  const [state, setState] = useState<GameState>(initialState);
  const [gameToken, setGameToken] = useState<string | null>(null);
  const api = useGameApi();

  // Initialize from URL hash on mount
  useEffect(() => {
    const hash = window.location.hash.slice(1); // Remove #
    if (hash.startsWith('/story/')) {
      const sessionId = hash.replace('/story/', '');
      if (sessionId) {
        loadGame(sessionId);
      }
    }

    // Parse token from URL query params
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token) {
      setGameToken(token);
    }
  }, []);

  const loadGame = useCallback(async (sessionId: string) => {
    try {
      const gameState = await api.getStory(sessionId);
      const latestScene = gameState.sceneHistory[gameState.sceneHistory.length - 1];
      
      setState({
        phase: gameState.story.status === 'completed' ? 'complete' :
               gameState.story.status === 'abandoned' ? 'abandoned' : 'playing',
        sessionId,
        story: gameState.story,
        character: gameState.character,
        currentScene: latestScene,
        choices: gameState.choices || [],
        sceneHistory: gameState.sceneHistory,
        waitingForRoll: false,
        pendingChoiceIndex: null,
        leveledUp: false,
        previousLevel: gameState.character.level,
      });

      // Update URL
      window.location.hash = `/story/${sessionId}`;
    } catch (err) {
      console.error('Failed to load game:', err);
      setState(initialState);
    }
  }, [api]);

  const startNewGame = useCallback(async (
    theme: string,
    characterName: string,
    characterClass: CharacterClass,
    provider?: 'nim' | 'openai' | 'ollama'
  ) => {
    try {
      const result = await api.createStory({
        theme,
        characterName,
        characterClass,
        provider,
        token: gameToken || undefined,
      });

      setState({
        phase: 'playing',
        sessionId: result.sessionId,
        story: result.story,
        character: result.character,
        currentScene: result.scene,
        choices: result.choices,
        sceneHistory: [result.scene],
        waitingForRoll: false,
        pendingChoiceIndex: null,
        leveledUp: false,
        previousLevel: 1,
      });

      // Update URL
      window.location.hash = `/story/${result.sessionId}`;
    } catch (err) {
      console.error('Failed to start game:', err);
      throw err;
    }
  }, [api, gameToken]);

  const makeChoice = useCallback(async (choiceIndex: number) => {
    if (!state.sessionId || !state.character) return;

    const choice = state.choices[choiceIndex];
    if (!choice) return;

    // If choice needs a roll, set up pending state
    if (choice.needsRoll && !state.waitingForRoll) {
      setState(prev => ({
        ...prev,
        waitingForRoll: true,
        pendingChoiceIndex: choiceIndex,
      }));
      return;
    }

    // Otherwise, proceed without roll
    await submitChoice(choiceIndex);
  }, [state.sessionId, state.character, state.choices, state.waitingForRoll]);

  const submitChoice = useCallback(async (choiceIndex: number, rollResult?: number, modifier?: number, statAbbrev?: string) => {
    if (!state.sessionId) return;

    try {
      const result = await api.continueStory(state.sessionId, choiceIndex, rollResult, modifier, statAbbrev);

      const previousLevel = result.previousLevel ?? state.character?.level ?? 1;
      const newLevel = result.character.level;
      const didLevelUp = result.leveledUp ?? (newLevel > previousLevel);

      setState(prev => ({
        ...prev,
        character: result.character,
        currentScene: result.scene,
        choices: result.choices,
        sceneHistory: [...prev.sceneHistory, result.scene],
        waitingForRoll: false,
        pendingChoiceIndex: null,
        leveledUp: didLevelUp,
        previousLevel: didLevelUp ? previousLevel : prev.previousLevel,
      }));
    } catch (err) {
      console.error('Failed to continue story:', err);
      throw err;
    }
  }, [state.sessionId, state.character, api]);

  const completeGame = useCallback(async () => {
    if (!state.sessionId) return;

    try {
      await api.completeStory(state.sessionId);
      setState(prev => ({ ...prev, phase: 'complete' }));
    } catch (err) {
      console.error('Failed to complete game:', err);
      throw err;
    }
  }, [state.sessionId, api]);

  const abandonGame = useCallback(async () => {
    if (!state.sessionId) return;

    try {
      await api.abandonStory(state.sessionId);
      setState(prev => ({ ...prev, phase: 'abandoned' }));
    } catch (err) {
      console.error('Failed to abandon game:', err);
      throw err;
    }
  }, [state.sessionId, api]);

  const startNewGameFromAbandoned = useCallback(() => {
    setState(initialState);
    window.location.hash = '';
  }, []);

  const updateCharacter = useCallback((character: Character) => {
    setState(prev => ({ ...prev, character }));
  }, []);

  const clearLevelUp = useCallback(() => {
    setState(prev => ({ ...prev, leveledUp: false }));
  }, []);

  return {
    ...state,
    loading: api.loading,
    error: api.error,
    startNewGame,
    makeChoice,
    submitChoice,
    completeGame,
    abandonGame,
    startNewGameFromAbandoned,
    updateCharacter,
    clearLevelUp,
  };
}
